import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { DollarSign, Clock, Users, AlertTriangle, TrendingUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

interface CostControlConfig {
  dailyBudget: number;
  monthlyBudget: number;
  rateLimitPerHour: number;
  rateLimitPerDay: number;
  enableBudgetAlerts: boolean;
  enableRateLimit: boolean;
  budgetAlertThreshold: number;
  userLimits: {
    admin: number;
    user: number;
  };
}

export default function CostControls() {
  const { toast } = useToast();
  
  const [config, setConfig] = useState<CostControlConfig>({
    dailyBudget: 50,
    monthlyBudget: 1000,
    rateLimitPerHour: 100,
    rateLimitPerDay: 1000,
    enableBudgetAlerts: true,
    enableRateLimit: true,
    budgetAlertThreshold: 80,
    userLimits: {
      admin: 500,
      user: 50
    }
  });

  // Mock current usage data
  const currentUsage = {
    dailySpent: 23.45,
    monthlySpent: 347.89,
    dailyRequests: 234,
    hourlyRequests: 12
  };

  const updateConfig = (field: string, value: any) => {
    setConfig(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const updateUserLimit = (role: string, value: number) => {
    setConfig(prev => ({
      ...prev,
      userLimits: {
        ...prev.userLimits,
        [role]: value
      }
    }));
  };

  const saveSettings = () => {
    toast({
      title: "Cost Controls Updated",
      description: "Budget and rate limiting settings have been saved.",
    });
  };

  const dailyBudgetUsage = (currentUsage.dailySpent / config.dailyBudget) * 100;
  const monthlyBudgetUsage = (currentUsage.monthlySpent / config.monthlyBudget) * 100;
  const hourlyRateUsage = (currentUsage.hourlyRequests / config.rateLimitPerHour) * 100;
  const dailyRateUsage = (currentUsage.dailyRequests / config.rateLimitPerDay) * 100;

  const getBudgetStatus = (usage: number) => {
    if (usage >= 90) return { color: "destructive", label: "Critical" };
    if (usage >= 75) return { color: "warning", label: "Warning" };
    if (usage >= 50) return { color: "primary", label: "Moderate" };
    return { color: "success", label: "Good" };
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h3 className="text-lg font-medium">Cost Controls & Rate Limiting</h3>
          <p className="text-sm text-muted-foreground">
            Manage budgets, rate limits, and usage controls
          </p>
        </div>
        <Button onClick={saveSettings}>
          Save Settings
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Daily Budget
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>${currentUsage.dailySpent}</span>
                <span>${config.dailyBudget}</span>
              </div>
              <Progress value={dailyBudgetUsage} className="h-2" />
              <Badge variant={getBudgetStatus(dailyBudgetUsage).color as any} className="text-xs">
                {getBudgetStatus(dailyBudgetUsage).label}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <TrendingUp className="h-4 w-4" />
              Monthly Budget
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>${currentUsage.monthlySpent}</span>
                <span>${config.monthlyBudget}</span>
              </div>
              <Progress value={monthlyBudgetUsage} className="h-2" />
              <Badge variant={getBudgetStatus(monthlyBudgetUsage).color as any} className="text-xs">
                {getBudgetStatus(monthlyBudgetUsage).label}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Hourly Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{currentUsage.hourlyRequests}</span>
                <span>{config.rateLimitPerHour}</span>
              </div>
              <Progress value={hourlyRateUsage} className="h-2" />
              <Badge variant={getBudgetStatus(hourlyRateUsage).color as any} className="text-xs">
                {getBudgetStatus(hourlyRateUsage).label}
              </Badge>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Users className="h-4 w-4" />
              Daily Rate
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between text-sm">
                <span>{currentUsage.dailyRequests}</span>
                <span>{config.rateLimitPerDay}</span>
              </div>
              <Progress value={dailyRateUsage} className="h-2" />
              <Badge variant={getBudgetStatus(dailyRateUsage).color as any} className="text-xs">
                {getBudgetStatus(dailyRateUsage).label}
              </Badge>
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Budget Configuration
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Daily Budget ($)</Label>
                <Input
                  type="number"
                  value={config.dailyBudget}
                  onChange={(e) => updateConfig('dailyBudget', parseFloat(e.target.value))}
                  min={0}
                  step={0.01}
                />
              </div>
              <div className="space-y-2">
                <Label>Monthly Budget ($)</Label>
                <Input
                  type="number"
                  value={config.monthlyBudget}
                  onChange={(e) => updateConfig('monthlyBudget', parseFloat(e.target.value))}
                  min={0}
                  step={0.01}
                />
              </div>
            </div>

            <Separator />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <div className="space-y-0.5">
                  <Label>Budget Alerts</Label>
                  <p className="text-xs text-muted-foreground">
                    Send notifications when approaching budget limits
                  </p>
                </div>
                <Switch
                  checked={config.enableBudgetAlerts}
                  onCheckedChange={(checked) => updateConfig('enableBudgetAlerts', checked)}
                />
              </div>

              {config.enableBudgetAlerts && (
                <div className="space-y-2">
                  <Label>Alert Threshold (%)</Label>
                  <Select
                    value={config.budgetAlertThreshold.toString()}
                    onValueChange={(value) => updateConfig('budgetAlertThreshold', parseInt(value))}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="50">50%</SelectItem>
                      <SelectItem value="75">75%</SelectItem>
                      <SelectItem value="80">80%</SelectItem>
                      <SelectItem value="90">90%</SelectItem>
                      <SelectItem value="95">95%</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-4 w-4" />
              Rate Limiting
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div className="space-y-0.5">
                <Label>Enable Rate Limiting</Label>
                <p className="text-xs text-muted-foreground">
                  Limit API requests to prevent overuse
                </p>
              </div>
              <Switch
                checked={config.enableRateLimit}
                onCheckedChange={(checked) => updateConfig('enableRateLimit', checked)}
              />
            </div>

            {config.enableRateLimit && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Requests per Hour</Label>
                    <Input
                      type="number"
                      value={config.rateLimitPerHour}
                      onChange={(e) => updateConfig('rateLimitPerHour', parseInt(e.target.value))}
                      min={1}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Requests per Day</Label>
                    <Input
                      type="number"
                      value={config.rateLimitPerDay}
                      onChange={(e) => updateConfig('rateLimitPerDay', parseInt(e.target.value))}
                      min={1}
                    />
                  </div>
                </div>

                <Separator />

                <div className="space-y-3">
                  <Label>User Role Limits (per day)</Label>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs">Admin Users</Label>
                      <Input
                        type="number"
                        value={config.userLimits.admin}
                        onChange={(e) => updateUserLimit('admin', parseInt(e.target.value))}
                        min={1}
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs">Regular Users</Label>
                      <Input
                        type="number"
                        value={config.userLimits.user}
                        onChange={(e) => updateUserLimit('user', parseInt(e.target.value))}
                        min={1}
                      />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {(dailyBudgetUsage > 90 || monthlyBudgetUsage > 90) && (
        <Card className="border-destructive">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-4 w-4" />
              Budget Alert
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">
              You are approaching or have exceeded your budget limits. Consider increasing budgets or reducing usage.
            </p>
          </CardContent>
        </Card>
      )}
    </div>
  );
}